import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { ApplicationRef, inject, Injectable, signal } from '@angular/core';
import { environment } from '@marineform/environment';
import {
  AppSubjectEnum,
  buildFilterGroupId,
  buildRealtimeEventName,
  IListEventData,
  IListSubscribeAck,
  IListSubscribeMessage,
  IRecordAvailabilityEventData,
  IRecordEventData,
  IRecordStatusEventData,
  ISessionClosingEventData,
  IWebsocketHandshakeErrorData,
  RealtimeChannelEnum,
  ClientWebsocketMessageEnum,
  WebsocketHandshakeErrorCodeEnum,
  WebsocketSessionCloseCodeEnum,
  WebsocketSessionMessageEnum,
  WebsocketTransportEnum,
} from '@splitsecondconnect/ultramarine-shared';
import { ModulesEnum } from '../enums/modules.enum';
import { mapModuleToSubject } from '../helpers/map-module-to-subject';
import { manageLogoutInFlight } from '../interceptors/logout-gate.interceptor';
import { refreshSession } from '../interceptors/session-request';
import { AuthStore } from '../stores/auth.store';
import { Socket } from 'ngx-socket-io';
import { firstValueFrom } from 'rxjs';
import { Observable, merge, Subject } from 'rxjs';
import { map } from 'rxjs/operators';

/**
 * Socket.IO client for the core-api `/ws` namespace.
 *
 * @description
 * Connect via `init()` after authentication so mutating HTTP requests can
 * attach the socket id. Replays active list subscriptions on reconnect.
 *
 * @example
 * ```typescript
 * const { subscriptionId, events$ } = ws.subscribeList({
 *   subject: AppSubjectEnum.VEHICLE,
 *   filters: { query: 'ABC' },
 * });
 * events$.subscribe((event) => console.log(event.disposition));
 * ws.unsubscribeList(subscriptionId);
 * ```
 */
@Injectable({ providedIn: 'root' })
export class WebsocketService extends Socket {
  readonly #http = inject(HttpClient);
  readonly #authStore = inject(AuthStore);

  #listSubscriptions = new Map<
    string,
    {
      subject: AppSubjectEnum;
      filters: Record<string, unknown>;
      groupId?: string;
      events$: Subject<IListEventData>;
    }
  >();
  #listFanoutSubjects = new Set<AppSubjectEnum>();
  readonly #listenersBound = signal(false);
  readonly #sessionRecoverySuppress = signal(false);
  readonly #rotationInFlight = signal(false);
  readonly #recentRefreshSucceeded = signal(false);
  readonly #sessionInvalidAfterRefreshRetries = signal(0);
  readonly #verifyUnavailableRetried = signal(false);
  #serverClosedExpired = false;

  constructor() {
    const wsBaseUrl = new URL(environment.ultramarineBaseUrl).origin;
    const config = {
      url: `${wsBaseUrl}${WebsocketTransportEnum.NAMESPACE}`,
      options: {
        // Each WebSocket gateway has a dedicated namespace; URL must include it.
        // Do not use the namespace option — ngx-socket-io ignores it.
        path: WebsocketTransportEnum.SOCKET_IO_PATH,
        withCredentials: true,
        transports: [WebsocketTransportEnum.TRANSPORT],
        autoConnect: false,
      },
    };
    super(config, inject(ApplicationRef));
    this.#bindSessionListenersOnce();
  }

  /**
   * Connect to the WebSocket server and replay list subscriptions on reconnect.
   * @description Call after auth login and before mutating HTTP requests.
   */
  init(): void {
    this.#sessionRecoverySuppress.set(false);
    this.connect();
  }

  /**
   * Subscribe to list opinions for a subject and filter set.
   * @param args - Subject and list-search filters, including `query`.
   * @returns Subscription id and observable of list opinion events.
   * @description
   * Precomputes a group id locally, then updates it from the server ack.
   * Fans out `{subject}/list` events by group id and skips events where
   * `excludedSubscriptionId` matches this subscription.
   */
  subscribeList(args: {
    subject: AppSubjectEnum;
    filters: Record<string, unknown>;
  }): {
    subscriptionId: string;
    events$: Observable<IListEventData>;
  } {
    const subscriptionId = crypto.randomUUID();
    const events$ = new Subject<IListEventData>();
    const groupId = buildFilterGroupId(args.filters);
    this.#listSubscriptions.set(subscriptionId, {
      subject: args.subject,
      filters: args.filters,
      groupId,
      events$,
    });
    this.#ensureListFanout(args.subject);
    this.emit(
      ClientWebsocketMessageEnum.LIST_SUBSCRIBE,
      {
        subscriptionId,
        subject: args.subject,
        filters: args.filters,
      } satisfies IListSubscribeMessage,
      (ack: IListSubscribeAck) => {
        const entry = this.#listSubscriptions.get(ack.subscriptionId);
        if (entry) {
          entry.groupId = ack.groupId;
        }
      },
    );
    return { subscriptionId, events$: events$.asObservable() };
  }

  /**
   * Tear down a prior list subscription.
   * @param subscriptionId - Id returned from `subscribeList`.
   */
  unsubscribeList(subscriptionId: string): void {
    this.emit(ClientWebsocketMessageEnum.LIST_UNSUBSCRIBE, { subscriptionId });
    const entry = this.#listSubscriptions.get(subscriptionId);
    if (entry) {
      entry.events$.complete();
      this.#listSubscriptions.delete(subscriptionId);
    }
  }

  /**
   * Record create, update, and delete events for a module.
   * @param module - Manage module to listen on.
   */
  onRecord(module: ModulesEnum): Observable<IRecordEventData> {
    return this.fromEvent<IRecordEventData>(
      buildRealtimeEventName(
        mapModuleToSubject(module),
        RealtimeChannelEnum.RECORD,
      ),
    );
  }

  /**
   * Record status change events for a module.
   * @param module - Manage module to listen on.
   */
  onRecordStatus(module: ModulesEnum): Observable<IRecordStatusEventData> {
    return this.fromEvent<IRecordStatusEventData>(
      buildRealtimeEventName(
        mapModuleToSubject(module),
        RealtimeChannelEnum.STATUS,
      ),
    );
  }

  onRecordStatusId(module: ModulesEnum): Observable<number> {
    return this.onRecordStatus(module).pipe(map((e) => e.id));
  }

  /**
   * Record availability change events for a module.
   * @param module - Manage module to listen on.
   */
  onRecordAvailability(
    module: ModulesEnum,
  ): Observable<IRecordAvailabilityEventData> {
    return this.fromEvent<IRecordAvailabilityEventData>(
      buildRealtimeEventName(
        mapModuleToSubject(module),
        RealtimeChannelEnum.AVAILABILITY,
      ),
    );
  }

  onRecordAvailabilityId(module: ModulesEnum): Observable<number> {
    return this.onRecordAvailability(module).pipe(map((e) => e.id));
  }

  onMultipleRecord(modules: ModulesEnum[]): Observable<IRecordEventData> {
    return merge(...modules.map((m) => this.onRecord(m)));
  }

  onAnyRecordStatus(modules: ModulesEnum[]): Observable<number> {
    return merge(...modules.map((m) => this.onRecordStatusId(m)));
  }

  #bindSessionListenersOnce(): void {
    if (this.#listenersBound()) {
      return;
    }
    this.#listenersBound.set(true);

    this.ioSocket.on('connect', () => this.#onConnect());
    this.ioSocket.on('connect_error', (error: unknown) =>
      this.#onConnectError(error),
    );
    this.ioSocket.on('disconnect', (reason: string) =>
      this.#onDisconnect(reason),
    );
    this.ioSocket.on(
      WebsocketSessionMessageEnum.SESSION_EXPIRING,
      () => this.#onSessionExpiring(),
    );
    this.ioSocket.on(
      WebsocketSessionMessageEnum.SESSION_CLOSING,
      (data: ISessionClosingEventData) => this.#onSessionClosing(data),
    );
  }

  #onSessionExpiring(): void {
    if (!this.#canRecover()) {
      return;
    }
    void this.#rotateSession();
  }

  #onConnect(): void {
    this.#serverClosedExpired = false;
    if (this.#recentRefreshSucceeded()) {
      this.#sessionInvalidAfterRefreshRetries.set(0);
    }
    this.#recentRefreshSucceeded.set(false);
    this.#rotationInFlight.set(false);
    this.#verifyUnavailableRetried.set(false);
    this.#replayListSubscriptions();
  }

  #onConnectError(error: unknown): void {
    if (!this.#canRecover()) {
      return;
    }

    if (this.ioSocket.active) {
      return;
    }

    this.#rotationInFlight.set(false);

    const code = (error as { data?: IWebsocketHandshakeErrorData }).data?.code;

    switch (code) {
      case WebsocketHandshakeErrorCodeEnum.SESSION_INVALID:
        void this.#handleSessionInvalidHandshake();
        break;
      case WebsocketHandshakeErrorCodeEnum.VERIFY_UNAVAILABLE:
        if (!this.#verifyUnavailableRetried()) {
          this.#verifyUnavailableRetried.set(true);
          this.connect();
        }
        break;
      case WebsocketHandshakeErrorCodeEnum.SESSION_REJECTED:
        this.#endSession();
        break;
      default:
        break;
    }
  }

  #onSessionClosing(data: ISessionClosingEventData): void {
    switch (data.code) {
      case WebsocketSessionCloseCodeEnum.SIGNED_OUT:
        this.#endSession();
        break;
      case WebsocketSessionCloseCodeEnum.EXPIRED:
        this.#serverClosedExpired = true;
        break;
      case WebsocketSessionCloseCodeEnum.TENANT_CHANGED:
        break;
      default:
        break;
    }
  }

  #onDisconnect(reason: string): void {
    if (reason !== 'io server disconnect' || !this.#serverClosedExpired) {
      return;
    }
    this.#serverClosedExpired = false;
    if (!this.#canRecover() || this.#rotationInFlight()) {
      return;
    }
    void this.#rotateSession();
  }

  async #handleSessionInvalidHandshake(): Promise<void> {
    if (
      this.#recentRefreshSucceeded() &&
      this.#sessionInvalidAfterRefreshRetries() < 1
    ) {
      this.#sessionInvalidAfterRefreshRetries.update((n) => n + 1);
      this.#recentRefreshSucceeded.set(false);
      await this.#rotateSession();
      return;
    }

    if (this.#recentRefreshSucceeded()) {
      this.#recentRefreshSucceeded.set(false);
      return;
    }

    await this.#rotateSession();
  }

  async #rotateSession(): Promise<void> {
    if (!this.#canRecover() || this.#rotationInFlight()) {
      return;
    }

    this.#rotationInFlight.set(true);

    try {
      await firstValueFrom(refreshSession(this.#http));
      if (!this.#canRecover()) {
        this.#rotationInFlight.set(false);
        return;
      }
      this.#recentRefreshSucceeded.set(true);
      if (this.ioSocket.connected) {
        this.disconnect();
      }
      this.connect();
    } catch (error: unknown) {
      this.#recentRefreshSucceeded.set(false);
      this.#rotationInFlight.set(false);

      if (error instanceof HttpErrorResponse && error.status === 401) {
        this.#endSession();
      }
    }
  }

  #canRecover(): boolean {
    return !manageLogoutInFlight() && !this.#sessionRecoverySuppress();
  }

  #endSession(): void {
    this.#sessionRecoverySuppress.set(true);
    this.#rotationInFlight.set(false);
    this.#recentRefreshSucceeded.set(false);
    this.#verifyUnavailableRetried.set(false);
    this.#serverClosedExpired = false;
    this.disconnect();
    this.#authStore.unauthenticate();
  }

  #ensureListFanout(subject: AppSubjectEnum): void {
    if (this.#listFanoutSubjects.has(subject)) {
      return;
    }

    this.#listFanoutSubjects.add(subject);
    this.fromEvent<IListEventData>(
      buildRealtimeEventName(subject, RealtimeChannelEnum.LIST),
    ).subscribe((event) => {
      for (const [subscriptionId, entry] of this.#listSubscriptions) {
        if (
          entry.subject === event.subject &&
          entry.groupId === event.groupId &&
          subscriptionId !== event.excludedSubscriptionId
        ) {
          entry.events$.next(event);
        }
      }
    });
  }

  #replayListSubscriptions(): void {
    for (const [subscriptionId, entry] of this.#listSubscriptions) {
      this.emit(
        ClientWebsocketMessageEnum.LIST_SUBSCRIBE,
        {
          subscriptionId,
          subject: entry.subject,
          filters: entry.filters,
        } satisfies IListSubscribeMessage,
        (ack: IListSubscribeAck) => {
          const current = this.#listSubscriptions.get(ack.subscriptionId);
          if (current) {
            current.groupId = ack.groupId;
          }
        },
      );
    }
  }
}
